/**
 * Pin Style picker — Step 2 (Creative) of the wizard.
 *
 * Only renders when the operator has selected the "Sponsored Map Pin"
 * campaign type. Lets them pick a category-specific marker design
 * (icon + colour) so e.g. a restaurant shows a fork-and-knife red pin
 * instead of the generic green star.
 *
 * Behaviour:
 *   • The picker auto-derives a default from the brand's category when
 *     a business is selected. The operator can change it but doesn't
 *     have to — the default is usually right.
 *   • Selection writes `pinStyle` on the wizard store, which flows into
 *     the live preview's Leaflet marker AND the RPC payload so the
 *     mobile clients render the same pin.
 *   • A "smart default" chip at the top resets to whatever the
 *     business's category implies — useful when an operator has been
 *     experimenting and wants to revert to the recommended pin.
 */

import { useMemo, useState } from 'react';
import { Check, Pin, Sparkles, ImageIcon, AlertCircle } from 'lucide-react';
import type { WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import {
  PIN_STYLES, PIN_STYLE_KEYS, resolvePinStyle, type PinStyleKey,
} from '@/lib/pinStyles';

interface PinStylePickerProps {
  mode: WizardMode;
}

export default function PinStylePicker({ mode }: PinStylePickerProps) {
  const store = useWizardStore(mode);
  const { campaignTypes, selectedBusiness, pinStyle, gallerySelectedLogoUrl } = store;

  const isMapPin = campaignTypes.includes('sponsored_map_pin');

  const recommended = useMemo(
    () => resolvePinStyle(null, selectedBusiness?.category ?? null),
    [selectedBusiness?.category],
  );

  // Track whether the brand's logo URL actually loaded — if it 404s
  // we want to surface that to the operator so they can fix it
  // before publishing rather than ship a broken pin to passengers.
  const [logoFailed, setLogoFailed] = useState(false);

  if (!isMapPin) return null;

  const active: PinStyleKey = (pinStyle as PinStyleKey | null) ?? recommended.key;
  const activeStyle = PIN_STYLES[active];
  const ActiveIcon = activeStyle.icon;

  // Logo URL preference: gallery pick (operator chose a logo on the
  // creative tab) > brand profile logo > nothing.
  const logoUrl = gallerySelectedLogoUrl || selectedBusiness?.logo_url || '';
  const showLogo = !!logoUrl && !logoFailed;

  return (
    <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center shrink-0">
            <Pin className="w-4 h-4 text-violet-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Map pin style</h3>
            <p className="text-[11px] text-gray-400">
              Pick a category icon & colour. Passengers and drivers see this
              marker on the map.
            </p>
          </div>
        </div>

        {/* Reset-to-recommended chip */}
        {pinStyle && pinStyle !== recommended.key && (
          <button
            type="button"
            className="text-[10px] text-violet-300 hover:text-violet-200 underline underline-offset-2 shrink-0"
            onClick={() => store.set({ pinStyle: recommended.key })}
          >
            Reset to recommended ({recommended.label})
          </button>
        )}
      </div>

      {/* Live pin preview — shows EXACTLY what passengers + drivers
          will see on the map. Built to mirror the FeaturedBusinessMarker
          and Leaflet preview pixel-for-pixel: white inner badge when a
          logo is present, category-coloured outer ring + tail, sponsored
          sparkle in the corner. */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 flex items-center gap-3">
        <div className="flex flex-col items-center shrink-0" style={{ filter: `drop-shadow(0 4px 8px ${activeStyle.bg}55)` }}>
          <div
            className="relative w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: activeStyle.bg, boxShadow: `0 0 0 2px ${activeStyle.bg}55` }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: showLogo ? '#ffffff' : activeStyle.bg }}
            >
              {showLogo ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="w-full h-full object-contain p-0.5"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <ActiveIcon className="w-4 h-4" style={{ color: activeStyle.fg }} />
              )}
            </div>
            <span
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 border-[1.5px] border-white flex items-center justify-center"
              title="Sponsored placement"
            >
              <Sparkles className="w-2 h-2 text-white" />
            </span>
          </div>
          <div
            className="w-0 h-0 -mt-0.5"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `9px solid ${activeStyle.bg}`,
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
            Live preview
            {showLogo ? (
              <span className="inline-flex items-center gap-1 text-emerald-400 normal-case font-medium">
                <ImageIcon className="w-3 h-3" /> brand logo on file
              </span>
            ) : logoUrl && logoFailed ? (
              <span className="inline-flex items-center gap-1 text-amber-400 normal-case font-medium">
                <AlertCircle className="w-3 h-3" /> logo failed to load &mdash; using icon
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-gray-500 normal-case font-medium">
                <AlertCircle className="w-3 h-3" /> no logo on file &mdash; using category icon
              </span>
            )}
          </div>
          <p className="text-sm text-white font-semibold truncate mt-0.5">
            {selectedBusiness?.name || 'Your brand'}
          </p>
          <p className="text-[10px] text-gray-500 leading-snug mt-0.5">
            {showLogo
              ? `Brand logo inside a white badge with a ${activeStyle.label.toLowerCase()} ring, the same pattern Google Maps uses for verified businesses.`
              : `Category icon (${activeStyle.label}). Add a logo on the Listings tab and this pin instantly switches to the branded version.`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {PIN_STYLE_KEYS.map((key) => {
          const s = PIN_STYLES[key];
          const Icon = s.icon;
          const isActive = active === key;
          const isRecommended = recommended.key === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => store.set({ pinStyle: key })}
              title={s.description}
              className={`
                group relative rounded-lg border px-2 py-2.5 text-left transition-all
                ${isActive
                  ? 'border-violet-400 bg-violet-500/15 ring-2 ring-violet-400/40'
                  : 'border-gray-700/60 bg-gray-900/40 hover:border-gray-600 hover:bg-gray-800/60'}
              `}
            >
              {isActive && (
                <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center shadow-md">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              )}

              <div className="flex items-center gap-2">
                {/* The actual marker preview — exact same shape that
                    will render on the map. Stripped to a small chip so
                    16+ options fit in a few rows. */}
                <div
                  className="w-7 h-7 rounded-full border-2 border-white/90 flex items-center justify-center shrink-0"
                  style={{ backgroundColor: s.bg, boxShadow: `0 0 0 1px ${s.bg}40` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: s.fg }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-white leading-tight truncate">
                    {s.label}
                  </div>
                  {isRecommended && (
                    <div className="text-[8px] text-emerald-400 font-bold uppercase tracking-wide leading-tight mt-0.5">
                      Auto
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[10px] text-gray-500 leading-relaxed">
        The pin updates instantly in the live preview &rarr; check the Map Pin tab.
        Recommended is auto-derived from <span className="font-semibold text-gray-400">
        {selectedBusiness?.category?.replace(/_/g, ' ') || 'business category'}</span>.
      </p>
    </div>
  );
}
